import {useRef, useState} from 'react'
import {
  Animated,
  PanResponder,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT} from '#/lib/slideTokens'
import {FeedbackBar} from '#/components/capsules/play/FeedbackBar'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {QuestionHeading} from '#/components/capsules/play/ThemeBits'
import {
  type FBState,
  type SlidePlayProps,
} from '#/components/capsules/play/types'
import {
  useFlashBookPlayPalette,
  useInkStroke,
  usePlayAccent,
  usePlayContentPadding,
  usePlayOptionTextStyle,
  usePlayPrimaryAction,
  usePlayRuleSnap,
  usePlayRuling,
  usePlaySelectionMode,
  usePlayStackGap,
  usePlaySuppressOwnSurface,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {useFlashBookInkSound} from '#/components/flashbooks/useFlashBookInkSound'
import {SLIDE_TYPE_META} from '#/types/capsule'

const ACCENT = SLIDE_ACCENT.drag_words

function DraggableWordChip({
  word,
  disabled,
  isSelected,
  onTap,
  onDrop,
}: {
  word: string
  disabled: boolean
  isSelected: boolean
  onTap: () => void
  onDrop: (pageX: number, pageY: number) => void
}) {
  const pan = useRef(new Animated.ValueXY()).current
  const palette = useFlashBookPlayPalette()
  const accent = usePlayAccent(ACCENT)
  // On ruled paper a chip is a whole number of rules tall, so a wrapping bank
  // of words keeps starting its rows on the paper's lines.
  const step = usePlayRuling()
  const snap = usePlayRuleSnap()
  // Where the theme marks with a highlighter, a word in the bank is a word on
  // the page — no pill around it.
  const highlight = usePlaySelectionMode() === 'highlight'
  const [dragging, setDragging] = useState(false)
  const dragStarted = useRef(false)
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => !disabled,
      onStartShouldSetPanResponderCapture: () => !disabled,
      onMoveShouldSetPanResponder: () => !disabled,
      onPanResponderGrant: () => {
        dragStarted.current = false
        pan.setValue({x: 0, y: 0})
      },
      onPanResponderMove: (_, g) => {
        if (!dragStarted.current && Math.hypot(g.dx, g.dy) > 6) {
          dragStarted.current = true
          setDragging(true)
        }
        if (dragStarted.current) {
          pan.setValue({x: g.dx, y: g.dy})
        }
      },
      onPanResponderRelease: (_, g) => {
        if (dragStarted.current) {
          onDrop(g.moveX, g.moveY)
        } else {
          onTap()
        }
        dragStarted.current = false
        setDragging(false)
        Animated.spring(pan, {
          toValue: {x: 0, y: 0},
          useNativeDriver: false,
          friction: 6,
        }).start()
      },
      onPanResponderTerminate: () => {
        dragStarted.current = false
        setDragging(false)
        Animated.spring(pan, {
          toValue: {x: 0, y: 0},
          useNativeDriver: false,
          friction: 6,
        }).start()
      },
    }),
  ).current

  return (
    <Animated.View
      accessibilityRole="button"
      {...panResponder.panHandlers}
      style={{
        transform: pan.getTranslateTransform(),
        zIndex: dragging ? 10 : 1,
        opacity: disabled ? 0.3 : 1,
      }}>
      <View
        style={[
          {
            paddingHorizontal: 14,
            paddingVertical: 8,
            borderRadius: 20,
            borderWidth: 1.5,
            borderColor: isSelected ? accent : accent + '66',
            backgroundColor: isSelected ? accent + '33' : accent + '18',
            elevation: dragging ? 6 : 0,
          },
          step > 0 && {
            height: snap(36),
            paddingVertical: 0,
            borderRadius: 10,
            justifyContent: 'center',
          },
          highlight && {
            borderWidth: 0,
            borderRadius: 0,
            backgroundColor: 'transparent',
            paddingHorizontal: 6,
          },
        ]}>
        <Text
          style={{
            color: isSelected ? accent : palette.body,
            fontSize: 14,
            fontWeight: '600',
          }}>
          {word}
        </Text>
      </View>
    </Animated.View>
  )
}

export function DragWordsSlidePlay({
  slide,
  onAnswer,
  primaryColor,
  retryKey,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const padding = usePlayContentPadding(24)
  // The words the learner places, and the bank they come from, wear the book's
  // own hand.
  const answerText = usePlayOptionTextStyle()
  // A whole number of the page's rules between the stacked sections.
  const stackGap = usePlayStackGap(16)
  /** One line of the page: a blank's own height on ruled paper. */
  const lineBox = answerText.lineHeight ?? 22
  // The check button is furniture on the page: a whole number of rules tall.
  // The action, in the page's own language: on paper it is ink, not a button.
  const action = usePlayPrimaryAction(49)
  // Whether this page prints on lines (the chip's own row pitch depends on it).
  const step = usePlayRuling()
  // Inside a book the block owns the surface — the sentence is printed on an
  // open or ruled page and framed by the block's panel otherwise.
  const onPaper = usePlaySuppressOwnSurface()
  const ink = useInkStroke()
  const inkSound = useFlashBookInkSound()

  const template = slide.dragWordsTemplate ?? ''
  const templateParts = template.split(/(\[[^\]]+\])/)
  const blanks = templateParts
    .filter(p => /^\[.+\]$/.test(p))
    .map(p => p.slice(1, -1))

  const allWords = useRef(
    [...blanks, ...(slide.dragWordsBank ?? [])].sort(() => Math.random() - 0.5),
  ).current

  const [fills, setFills] = useState<(string | null)[]>(
    Array(blanks.length).fill(null),
  )
  const [selectedWord, setSelectedWord] = useState<string | null>(null)
  const [checked, setChecked] = useState(false)
  const [retried, setRetried] = useState(false)
  const [showSol, setShowSol] = useState(false)
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)
  const blankRefs = useRef<Record<number, View | null>>({})

  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setFills(Array(blanks.length).fill(null))
    setSelectedWord(null)
    setChecked(false)
    setRetried(false)
    setShowSol(false)
  }

  let blankIdx = 0

  const fillBlank = (idx: number) => {
    if (checked) return
    if (selectedWord) {
      setFills(f => {
        const n = [...f]
        n[idx] = selectedWord
        return n
      })
      setSelectedWord(null)
    } else if (fills[idx]) {
      setSelectedWord(fills[idx])
      setFills(f => {
        const n = [...f]
        n[idx] = null
        return n
      })
    }
  }

  const measureBlank = (
    idx: number,
  ): Promise<{x: number; y: number; width: number; height: number} | null> =>
    new Promise(resolve => {
      const node = blankRefs.current[idx]
      if (!node) {
        resolve(null)
        return
      }
      // @ts-ignore RN View has measureInWindow at runtime
      node.measureInWindow(
        (x: number, y: number, width: number, height: number) =>
          resolve({x, y, width, height}),
      )
    })

  const tryDrop = async (word: string, pageX: number, pageY: number) => {
    if (checked) return
    for (let idx = 0; idx < blanks.length; idx++) {
      const rect = await measureBlank(idx)
      if (
        rect &&
        pageX >= rect.x &&
        pageX <= rect.x + rect.width &&
        pageY >= rect.y &&
        pageY <= rect.y + rect.height
      ) {
        setFills(f => {
          const n = [...f]
          n[idx] = word
          return n
        })
        setSelectedWord(null)
        return
      }
    }
  }

  const allFilled = fills.every(f => f !== null)

  const isSlotCorrect = (i: number) =>
    fills[i]?.toLowerCase().trim() === blanks[i]?.toLowerCase().trim()

  const okCount = fills.filter((_, i) => isSlotCorrect(i)).length
  const maxPoints = blanks.length
  const fbState: FBState = showSol
    ? 'solution'
    : okCount === maxPoints
      ? 'correct'
      : okCount > 0
        ? 'partial'
        : 'wrong'

  const handleCheck = () => {
    onAnswer({
      isCorrect: okCount === maxPoints,
      points: okCount,
      maxPoints,
      solutionText: blanks.join(' · '),
      explanation: slide.explanation,
      userAnswer: fills,
    })
    setChecked(true)
  }

  const retry = () => {
    setFills(Array(blanks.length).fill(null))
    setSelectedWord(null)
    setChecked(false)
    setRetried(true)
  }

  const palette = useFlashBookPlayPalette()
  // Inside a book every interactive affordance wears the book's own accent.
  const accent = usePlayAccent(ACCENT)

  // Determine displayed fill value (solution overrides current fill)
  const displayFill = (i: number): string | null =>
    showSol ? blanks[i] : fills[i]

  const slotBorderColor = (i: number) => {
    if (showSol) return palette.warning
    if (!checked) {
      if (displayFill(i)) return accent + '99'
      if (selectedWord) return accent
      return accent + '66'
    }
    return isSlotCorrect(i) ? palette.positive : palette.negative
  }

  const slotTextColor = (i: number) => {
    if (showSol) return palette.warning
    if (!checked) return displayFill(i) ? palette.inputText : accent + 'aa'
    return isSlotCorrect(i) ? palette.positive : palette.negative
  }

  return (
    <PlayScroller
      style={ss.scroll}
      contentContainerStyle={[ss.content, {padding, gap: stackGap}]}
      keyboardShouldPersistTaps="handled">
      {!!slide.question && (
        <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
          {slide.question}
        </QuestionHeading>
      )}

      {/* Paragraph with inline blank slots — bare prose on open themes. */}
      <View
        style={[
          ss.paragraphCard,
          onPaper
            ? {
                backgroundColor: 'transparent',
                // The frame goes, not just its colour: two transparent border
                // pixels are two pixels of height the ruling does not have.
                borderWidth: 0,
                padding: 0,
              }
            : {
                backgroundColor: palette.surfaceAlt,
                borderColor: palette.border,
              },
        ]}>
        <View style={[ss.inlineRow, onPaper && {rowGap: 0}]}>
          {templateParts.map((part, pi) => {
            if (/^\[.+\]$/.test(part)) {
              const bi = blankIdx++
              const fill = displayFill(bi)
              return (
                <TouchableOpacity
                  accessibilityRole="button"
                  key={pi}
                  onPress={() => fillBlank(bi)}
                  disabled={checked}
                  ref={el => {
                    blankRefs.current[bi] = el
                  }}
                  style={[
                    ss.slot,
                    {
                      borderStyle: fill
                        ? 'solid'
                        : checked
                          ? 'solid'
                          : selectedWord
                            ? 'solid'
                            : 'dashed',
                      borderColor: slotBorderColor(bi),
                      backgroundColor: showSol
                        ? palette.warning + '11'
                        : checked
                          ? isSlotCorrect(bi)
                            ? palette.positive + '18'
                            : palette.negative + '18'
                          : fill
                            ? accent + '18'
                            : selectedWord
                              ? accent + '22'
                              : palette.surface,
                    },
                    // On ruled paper a blank is a space to write on, one rule
                    // tall: a floating 34px box between two lines is the thing
                    // that makes a book look like a form.
                    onPaper && {
                      height: lineBox,
                      borderRadius: 0,
                      borderWidth: 0,
                      borderBottomWidth: 2,
                      backgroundColor: 'transparent',
                    },
                  ]}>
                  <Text
                    style={[
                      ss.slotText,
                      answerText,
                      {color: slotTextColor(bi)},
                    ]}>
                    <Text
                      style={ink(
                        fill
                          ? showSol
                            ? palette.warning
                            : checked
                              ? isSlotCorrect(bi)
                                ? palette.positive
                                : palette.negative
                              : accent
                          : null,
                      )}>
                      {fill ?? '   '}
                    </Text>
                  </Text>
                </TouchableOpacity>
              )
            }
            return part
              .trim()
              .split(/\s+/)
              .filter(Boolean)
              .map((word, wi) => (
                <Text
                  key={`t${pi}w${wi}`}
                  style={[ss.word, answerText, {color: palette.body}]}>
                  {word}
                </Text>
              ))
          })}
        </View>
      </View>

      {/* Word bank — hidden once checked */}
      {!checked && !showSol && (
        <View style={[ss.bankSection, step > 0 && {gap: stackGap}]}>
          <Text style={[ss.bankLabel, answerText, {color: palette.muted}]}>
            WORD BANK
          </Text>
          <View style={[ss.bank, step > 0 && {rowGap: 0}]}>
            {allWords.map((word, wi) => {
              const timesInBlanks = fills.filter(f => f === word).length
              const isUsed =
                timesInBlanks >= allWords.filter(w => w === word).length
              const isSel = word === selectedWord
              return (
                <DraggableWordChip
                  key={wi}
                  word={word}
                  disabled={isUsed && !isSel}
                  isSelected={isSel}
                  onTap={() => {
                    if (isUsed && !isSel) return
                    inkSound.mark()
                    setSelectedWord(isSel ? null : word)
                  }}
                  onDrop={(pageX, pageY) => {
                    if (isUsed && !isSel) return
                    inkSound.mark()
                    void tryDrop(word, pageX, pageY)
                  }}
                />
              )
            })}
          </View>
          {selectedWord && (
            <Text style={[ss.selectionHint, {color: accent}]}>
              "{selectedWord}" — tap a blank to place it
            </Text>
          )}
        </View>
      )}

      {!checked && !showSol && (
        <TouchableOpacity
          style={[
            ss.checkBtn,
            {backgroundColor: allFilled ? primaryColor : palette.surfaceAlt},
            action.style,
          ]}
          onPress={handleCheck}
          disabled={!allFilled}
          accessibilityRole="button"
          accessibilityLabel={l`Check answer`}
          accessibilityHint={undefined}>
          <Feather
            name="check"
            size={16}
            color={
              action.ink(allFilled) ??
              (allFilled ? palette.onAccent : palette.muted)
            }
          />
          <Text
            style={[
              ss.checkBtnText,
              {
                color:
                  action.ink(allFilled) ??
                  (allFilled ? palette.onAccent : palette.muted),
              },
              action.labelStyle(allFilled),
            ]}>
            <Trans>Check</Trans>
          </Text>
        </TouchableOpacity>
      )}

      {checked && (
        <FeedbackBar
          state={fbState}
          points={okCount}
          maxPoints={maxPoints}
          explanation={!showSol ? slide.explanation : undefined}
          solutionText={blanks.join(' · ')}
          onRetry={!retried ? retry : undefined}
          onShowSolution={
            !showSol
              ? () => {
                  setShowSol(true)
                  setFills(blanks.map(b => b))
                }
              : undefined
          }
        />
      )}
    </PlayScroller>
  )
}

const ss = StyleSheet.create({
  scroll: {flex: 1},
  content: {padding: 24, gap: 16, paddingBottom: 40},
  question: {
    fontSize: 20,
    fontWeight: '700',
    color: PLAY_SURFACE.text,
    lineHeight: 28,
    marginTop: 8,
  },
  paragraphCard: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    padding: 16,
  },
  inlineRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
    rowGap: 8,
  },
  word: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.85)',
    lineHeight: 22,
  },
  slot: {
    minWidth: 80,
    height: 34,
    borderRadius: 8,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
  slotText: {fontSize: 13, fontWeight: '700'},
  bankSection: {gap: 8},
  bankLabel: {
    color: PLAY_SURFACE.textFaint,
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.9,
  },
  bank: {flexDirection: 'row', flexWrap: 'wrap', gap: 8},
  selectionHint: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  checkBtn: {
    flexDirection: 'row',
    borderRadius: 14,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  checkBtnText: {color: '#fff', fontSize: 16, fontWeight: '700'},
  checkBtnDisabled: {color: 'rgba(255,255,255,0.4)'},
})
